import { z } from "zod";
import { IsoDateTimeSchema } from "../common/primitives.js";
import { RawConditionSchema, ResearchPersonSchema } from "../research/research.js";
/**
 * Manual research (D-64): an associate adds a card by name, then every listing URL found for it online,
 * each with its own price and condition. Each link can be sent to the admin, who approves (amount editable)
 * or declines with a reason.
 */
const Decimal = z.string().regex(/^\d{1,14}\.\d{2,4}$/);
const typedAmount = (what) => z
    .string(`Enter the ${what}`)
    .trim()
    .regex(/^\d{1,9}(\.\d{1,2})?$/, `Enter the ${what} as a number, e.g. 12.50`)
    .refine((v) => Number(v) > 0, `The ${what} must be more than 0`);
const Currency = z
    .string("Choose the currency")
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, "Choose the currency");
const Notes = z
    .string()
    .trim()
    .max(2000, "At most 2000 characters")
    .nullish()
    .transform((v) => (v === undefined || v === null || v === "" ? null : v));
export const CardNameSchema = z
    .string("Enter the card name")
    .trim()
    .min(1, "Enter the card name")
    .max(120, "The card name must be at most 120 characters");
const OptionalText = (max, what) => z
    .string()
    .trim()
    .max(max, `The ${what} must be at most ${String(max)} characters`)
    .nullish()
    .transform((v) => (v === undefined || v === null || v === "" ? null : v));
/** POST /api/v1/cards and PATCH /api/v1/cards/:id */
export const CardInputSchema = z.object({
    name: CardNameSchema,
    /** Collection or set, e.g. "Roaring Skies" (D-67). */
    setName: OptionalText(120, "collection"),
});
/** Where a listing was found (D-67). A physical store has no URL. */
/** The platform is not chosen: it is detected from the listing URL (D-69, see platform.ts), e.g. "eBay". */
export const PlatformNameSchema = z.string().trim().min(1).max(60);
export const SELLER_TYPES = ["shop", "private"];
export const SellerTypeSchema = z.enum(SELLER_TYPES);
/**
 * The admin's working status for a listing (D-67), next to the approval (Ask admin) status.
 * "purchased" adds the card to the Portfolio; "closed" hides the listing.
 */
export const DEAL_STATUSES = [
    "open",
    "opened",
    "negotiating",
    "no_buy",
    "buying",
    "purchased",
    "closed",
];
export const DealStatusSchema = z.enum(DEAL_STATUSES);
export const LINK_PRIORITIES = ["top", "high", "mid", "low"];
export const LinkPrioritySchema = z.enum(LINK_PRIORITIES);
/** PATCH .../links/:linkId/deal — status and priority (admin), price paid (admin or who added it). */
export const LinkDealRequestSchema = z.object({
    dealStatus: DealStatusSchema.optional(),
    priority: LinkPrioritySchema.nullable().optional(),
    pricePaid: z
        .string()
        .trim()
        .regex(/^\d{1,9}(\.\d{1,2})?$/, "Enter the price paid as a number, e.g. 12.50")
        .nullish()
        .or(z.literal("").transform(() => null)),
});
/** POST /api/v1/cards/:id/closed — close a card (hides it and its listings) or open it again. */
export const CardClosedRequestSchema = z.object({ closed: z.boolean() });
export const ITEM_STATES = ["new", "used"];
export const ItemStateSchema = z.enum(ITEM_STATES);
export const LINK_STATUSES = ["draft", "waiting", "approved", "declined"];
export const LinkStatusSchema = z.enum(LINK_STATUSES);
/** POST /api/v1/cards/:id/links and PUT /api/v1/cards/:id/links/:linkId */
export const CardLinkInputSchema = z
    .object({
    /** Found in a shop you can walk into: no URL, the store's name (and address) instead. */
    physicalStore: z.boolean().default(false),
    url: z
        .string()
        .trim()
        .max(2000, "The URL is too long")
        .nullish()
        .transform((v) => (v === undefined || v === null || v === "" ? null : v)),
    storeName: OptionalText(200, "store name"),
    storeAddress: OptionalText(300, "address"),
    sellerType: SellerTypeSchema.default("shop"),
    pricePaid: z
        .string()
        .trim()
        .regex(/^\d{1,9}(\.\d{1,2})?$/, "Enter the price paid as a number, e.g. 12.50")
        .nullish()
        .or(z.literal("").transform(() => null)),
    /** No fixed price: the seller asks for bids (D-68). Then the price is left empty. */
    openToBids: z.boolean().default(false),
    price: z.preprocess((v) => (v === "" ? null : v), typedAmount("price").nullish()),
    currency: Currency,
    condition: RawConditionSchema,
    itemState: ItemStateSchema,
    scratches: z.boolean().default(false),
    whitening: z.boolean().default(false),
    bends: z.boolean().default(false),
    surfaceDamage: z.boolean().default(false),
    notes: Notes,
})
    .superRefine((v, ctx) => {
    if (!v.openToBids && (v.price === null || v.price === undefined))
        ctx.addIssue({ code: "custom", path: ["price"], message: "Enter the price, or tick “Open to bids”" });
    if (v.physicalStore) {
        if (v.storeName === null)
            ctx.addIssue({ code: "custom", path: ["storeName"], message: "Enter the store name" });
    }
    else if (v.url === null) {
        ctx.addIssue({ code: "custom", path: ["url"], message: "Enter the listing URL" });
    }
    else if (!/^https?:\/\/[^\s/$.?#][^\s]*$/i.test(v.url)) {
        ctx.addIssue({ code: "custom", path: ["url"], message: "Enter the full link, starting with https://" });
    }
})
    .transform((v) => ({
    ...(v.physicalStore ? { ...v, url: null } : { ...v, storeName: null, storeAddress: null }),
    price: v.openToBids ? null : (v.price ?? null),
}));
/** POST .../links/:linkId/approve — the admin may change the amount before approving. */
export const ApproveLinkRequestSchema = z.object({
    approvedAmount: typedAmount("approved amount"),
    note: Notes,
});
/** POST .../links/:linkId/decline */
export const DeclineLinkRequestSchema = z.object({
    reason: z
        .string("Say why it is declined")
        .trim()
        .min(1, "Say why it is declined")
        .max(2000, "At most 2000 characters"),
});
export const CardLinkSchema = z.object({
    id: z.uuid(),
    cardId: z.uuid(),
    url: z.string().nullable(),
    /** The website, or the store name for a physical store. */
    website: z.string(),
    physicalStore: z.boolean(),
    storeName: z.string().nullable(),
    storeAddress: z.string().nullable(),
    /** Detected from the URL, e.g. "eBay", "Finn", "Marktplaats"; "Physical store" for a shop visit. */
    platform: z.string(),
    sellerType: SellerTypeSchema,
    pricePaid: Decimal.nullable(),
    dealStatus: DealStatusSchema,
    priority: LinkPrioritySchema.nullable(),
    /** The Portfolio card made when it was purchased. */
    portfolioItemId: z.uuid().nullable(),
    /** null: no fixed price, the seller takes bids (openToBids). */
    price: Decimal.nullable(),
    openToBids: z.boolean(),
    currency: z.string(),
    /** The price in US dollars at today's rate; null when the currency cannot be converted. */
    priceUsd: Decimal.nullable(),
    condition: RawConditionSchema,
    itemState: ItemStateSchema,
    scratches: z.boolean(),
    whitening: z.boolean(),
    bends: z.boolean(),
    surfaceDamage: z.boolean(),
    notes: z.string().nullable(),
    status: LinkStatusSchema,
    askedAt: IsoDateTimeSchema.nullable(),
    decision: z
        .object({
        outcome: z.enum(["approved", "declined"]),
        approvedAmount: Decimal.nullable(),
        note: z.string().nullable(),
        decidedBy: ResearchPersonSchema,
        decidedAt: IsoDateTimeSchema,
    })
        .nullable(),
    addedBy: ResearchPersonSchema,
    addedAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
});
const count = z.number().int().min(0);
export const TrackedCardSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    setName: z.string().nullable(),
    /** Changes whenever the image changes (null: no image); use it in the image address to refresh it. */
    imageVersion: z.string().nullable(),
    closed: z.boolean(),
    addedBy: ResearchPersonSchema,
    addedAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    linkCount: count,
    statusCounts: z.object({ draft: count, waiting: count, approved: count, declined: count }),
});
export const CARDS_PAGE_SIZE = 25;
/** GET /api/v1/cards?q=pikachu&scope=mine&page=2 — most recently changed first. */
export const CardListQuerySchema = z.object({
    q: z
        .string()
        .trim()
        .max(100, "Search is too long")
        .optional()
        .transform((v) => (v === "" ? undefined : v)),
    scope: z.enum(["all", "mine"]).default("all"),
    /** Closed cards are hidden unless asked for. */
    closed: z.stringbool().default(false),
    page: z.coerce.number().int().min(1).max(10_000).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(CARDS_PAGE_SIZE),
});
export const CardListResponseSchema = z.object({
    data: z.array(TrackedCardSchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
});
export const CardDetailResponseSchema = z.object({
    card: TrackedCardSchema,
    links: z.array(CardLinkSchema),
});
/** GET /api/v1/cards/requests — every link waiting for the admin, oldest first. */
export const LinkRequestSchema = z.object({ cardName: z.string(), link: CardLinkSchema });
export const LinkRequestsResponseSchema = z.object({ data: z.array(LinkRequestSchema) });
export const NotificationSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    body: z.string(),
    /** Where clicking it goes in the web app, e.g. "/cards/<id>". */
    link: z.string().nullable(),
    readAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
});
/** GET /api/v1/notifications — the latest 30, and how many are unread. */
export const NotificationListResponseSchema = z.object({
    data: z.array(NotificationSchema),
    unread: count,
});
/** GET /api/v1/cards/listings — every listing across all cards, filtered (newest first). */
export const ListingQuerySchema = z.object({
    q: z
        .string()
        .trim()
        .max(100, "Search is too long")
        .optional()
        .transform((v) => (v === "" ? undefined : v)),
    status: LinkStatusSchema.optional(),
    condition: RawConditionSchema.optional(),
    itemState: ItemStateSchema.optional(),
    platform: PlatformNameSchema.optional(),
    dealStatus: DealStatusSchema.optional(),
    priority: LinkPrioritySchema.optional(),
    /** Words in the card's collection/set. */
    setName: z
        .string()
        .trim()
        .max(120, "Too long")
        .optional()
        .transform((v) => (v === "" ? undefined : v)),
    /**
     * The working list (D-67): "active" = open, opened, no buy and buying; "negotiating", "purchased" and "closed"
     * are their own lists. Closed listings show only in "closed".
     */
    view: z.enum(["active", "negotiating", "purchased", "closed", "all"]).default("active"),
    sort: z.enum(["latest", "price_asc", "price_desc", "priority"]).default("latest"),
    scope: z.enum(["all", "mine"]).default("all"),
    page: z.coerce.number().int().min(1).max(10_000).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(CARDS_PAGE_SIZE),
});
export const ListingListResponseSchema = z.object({
    data: z.array(LinkRequestSchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
});
/** GET /api/v1/cards/stats — totals for the dashboard (whole team, or mine). */
export const CardStatsSchema = z.object({
    cards: count,
    listings: count,
    byStatus: z.object({ draft: count, waiting: count, approved: count, declined: count }),
    byDeal: z.object({
        open: count,
        opened: count,
        negotiating: count,
        no_buy: count,
        buying: count,
        purchased: count,
        closed: count,
    }),
    byCondition: z.object({
        near_mint: count,
        lightly_played: count,
        moderately_played: count,
        heavily_played: count,
        damaged: count,
    }),
});
//# sourceMappingURL=cards.js.map